import { useEffect, useState } from "react";
import qrcode from "qrcode-generator";
import { useTranslation } from "react-i18next";

import {
  fetchKioskPresenceCode,
  KioskPunchError,
  type KioskPresenceCodeFetcher,
} from "@/api/punch";
import { useNow } from "@/lib/useNow";

interface DisplayCode {
  readonly path: string;
  readonly modules: number;
  readonly expiresAt: number;
}

/** Whether the terminal can currently show a valid presence code. */
export type QrHealth = "pending" | "ok" | "failed";

export interface QrScreenProps {
  readonly fetchPresenceCode?: KioskPresenceCodeFetcher | undefined;
  readonly onHealthChange?: ((health: QrHealth) => void) | undefined;
}

/** QR-first idle panel. The only encoded content is the server's signed code. */
export function QrScreen({
  fetchPresenceCode = fetchKioskPresenceCode,
  onHealthChange,
}: QrScreenProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  useNow();
  const [code, setCode] = useState<DisplayCode | null>(null);
  const [pending, setPending] = useState(true);
  const [expired, setExpired] = useState(false);
  const [failure, setFailure] = useState<KioskPunchError | null>(null);
  const [retryAt, setRetryAt] = useState(0);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let inFlight = false;
    let activeExpiresAt = 0;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    let expiryTimer: ReturnType<typeof setTimeout> | undefined;

    const load = async (): Promise<void> => {
      if (inFlight || cancelled) return;
      inFlight = true;
      setPending(true);
      setFailure(null);
      try {
        const next = await fetchPresenceCode();
        if (cancelled) return;
        const expiresAt = Date.parse(next.expires_at);
        if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
          setCode(null);
          setExpired(true);
          return;
        }

        // The maintained encoder owns QR versions, masking and error correction.
        // Render its modules as SVG with a four-module quiet zone; no HTML injection.
        const qr = qrcode(0, "M");
        qr.addData(next.presence_code, "Byte");
        qr.make();
        const modules = qr.getModuleCount();
        const path: string[] = [];
        for (let row = 0; row < modules; row += 1) {
          for (let column = 0; column < modules; column += 1) {
            if (qr.isDark(row, column)) path.push(`M${column},${row}h1v1h-1z`);
          }
        }
        clearTimeout(refreshTimer);
        clearTimeout(expiryTimer);
        activeExpiresAt = expiresAt;
        setCode({ path: path.join(""), modules, expiresAt });
        setExpired(false);
        const remaining = expiresAt - Date.now();
        // Reserve the last fifth of the server TTL for refresh latency. Expiry
        // is independent: a slow/hung refresh must never leave an old QR visible.
        refreshTimer = setTimeout(() => { void load(); }, remaining * 0.8);
        expiryTimer = setTimeout(() => {
          setCode(null);
          setExpired(true);
        }, remaining);
      } catch (error) {
        if (cancelled) return;
        clearTimeout(refreshTimer);
        clearTimeout(expiryTimer);
        setCode(null);
        const known = error instanceof KioskPunchError ? error : null;
        setFailure(known);
        setRetryAt(Date.now() + (known?.retryAfterMs ?? 0));
      } finally {
        inFlight = false;
        if (!cancelled) setPending(false);
      }
    };

    // Background tabs may throttle timers. Clear an expired code as soon as
    // the terminal becomes visible, even if the refresh is still outstanding.
    const onVisibility = (): void => {
      if (document.hidden || activeExpiresAt === 0 || activeExpiresAt > Date.now()) return;
      setCode(null);
      setExpired(true);
      void load();
    };
    document.addEventListener("visibilitychange", onVisibility);
    void load();
    return () => {
      cancelled = true;
      clearTimeout(refreshTimer);
      clearTimeout(expiryTimer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchPresenceCode, attempt]);

  // Also guard the render itself, so no unrelated re-render can expose a stale QR.
  const visibleCode = code !== null && code.expiresAt > Date.now() ? code : null;
  const health: QrHealth = visibleCode !== null ? "ok" : pending ? "pending" : "failed";
  useEffect(() => { onHealthChange?.(health); }, [health, onHealthChange]);
  const retrySeconds = Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
  const failureClassificationKey = failure?.classification !== undefined
    ? `errors.classification.${failure.classification}.fallback`
    : "errors.unknown.fallback";
  const expiryTime = visibleCode === null ? null : new Intl.DateTimeFormat(i18n.language, {
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).format(visibleCode.expiresAt);

  return (
    <section className="mt-5 flex w-full max-w-[380px] flex-col items-center gap-3 text-center">
      <h2 className="m-0 font-sans text-[20px] font-semibold text-[var(--fg-primary)]">
        {t("kiosk.qr.title")}
      </h2>
      <div className="flex aspect-square w-[min(280px,62vw,34dvh)] items-center justify-center rounded-2xl border border-[var(--border-subtle)] bg-[var(--bg-raised)] p-3">
        {visibleCode !== null ? (
          <svg
            role="img"
            aria-label={t("kiosk.qr.image_label")}
            viewBox={`-4 -4 ${visibleCode.modules + 8} ${visibleCode.modules + 8}`}
            shapeRendering="crispEdges"
            className="h-full w-full fill-[var(--fg-primary)]"
          >
            <path d={visibleCode.path} />
          </svg>
        ) : (
          <p role="status" className="px-3 font-serif text-[18px] text-[var(--fg-secondary)]">
            {expired ? t("kiosk.qr.expired") : pending ? t("kiosk.qr.loading") : t("kiosk.qr.unavailable")}
          </p>
        )}
      </div>
      <p className="m-0 font-serif text-[16px] text-[var(--fg-secondary)]">
        {visibleCode !== null
          ? t("kiosk.qr.valid_until", { time: expiryTime })
          : failure !== null
            ? t([failure.messageKey, failureClassificationKey, "errors.unknown.fallback"])
            : t("kiosk.qr.hint")}
      </p>
      {visibleCode === null && !pending && failure?.retryable !== false && (
        <button
          type="button"
          disabled={retrySeconds > 0}
          onClick={() => { setExpired(false); setAttempt((value) => value + 1); }}
          className="min-h-11 cursor-pointer rounded-full border border-[var(--border-default)] px-5 font-sans text-[15px] font-semibold text-[var(--fg-primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {retrySeconds > 0 ? t("kiosk.failure.retry_after", { seconds: retrySeconds }) : t("kiosk.qr.retry")}
        </button>
      )}
    </section>
  );
}
